import React, { useState, useEffect, useCallback, useMemo } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Alert,
  LinearProgress,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  List,
  ListItem,
  Avatar,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import "./UserRoleManagement.css";
import { getToken } from "services/auth.service";
import { usePageTitle } from "context/PageTitleContext";
import api from "services/api";
import { useAuth } from "context/AuthContext";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import UserTable from "../../components/users/UserTable";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import { getRoleColor } from "modules/systems/utils/permission.utils";
import DataTable from "components/common/tables/DataTable";
import { AppAutocomplete, AppTextField } from "components/common/inputs";
import { AppButton } from "components/common/buttons";
import { AppDialog, useConfirm } from "components/common/dialogs";

const UserRoleManagement = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const { setPageTitle } = usePageTitle();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [applications, setApplications] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [userContracts, setUserContracts] = useState([]);
  const [allUserContracts, setAllUserContracts] = useState([]); // Tất cả hợp đồng của user để lọc
  const [contracts, setContracts] = useState([]);
  const [userSubcontracts, setUserSubcontracts] = useState([]);
  const [allUserSubcontracts, setAllUserSubcontracts] = useState([]);
  const [subcontracts, setSubcontracts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  // single selectedContract removed - using multi-select selectedContracts instead
  const [selectedContracts, setSelectedContracts] = useState([]); // array of contractId
  const [contractFilter, setContractFilter] = useState(true);
  const [rowSelection, setRowSelection] = useState({});
  const [addContractsOpen, setAddContractsOpen] = useState(false);
  const [addRowSelection, setAddRowSelection] = useState({});
  const [addSubcontractsOpen, setAddSubcontractsOpen] = useState(false);
  const [addSubcontractRowSelection, setAddSubcontractRowSelection] =
    useState({});

  // States for contract pagination in modal
  const [contractPagination, setContractPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [contractsTotal, setContractsTotal] = useState(0);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsGlobalFilter, setContractsGlobalFilter] = useState("");
  const [subcontractPagination, setSubcontractPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [subcontractsTotal, setSubcontractsTotal] = useState(0);
  const [subcontractsLoading, setSubcontractsLoading] = useState(false);
  const [subcontractsGlobalFilter, setSubcontractsGlobalFilter] = useState("");

  // States for user contracts pagination - Server-side
  const [userContractsPagination, setUserContractsPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const [userContractsTotal, setUserContractsTotal] = useState(0);
  const [userContractsLoading, setUserContractsLoading] = useState(false);
  const [userContractsGlobalFilter, setUserContractsGlobalFilter] =
    useState("");
  const [userSubcontractsPagination, setUserSubcontractsPagination] =
    useState({
      pageIndex: 0,
      pageSize: 5,
    });
  const [userSubcontractsTotal, setUserSubcontractsTotal] = useState(0);
  const [userSubcontractsLoading, setUserSubcontractsLoading] =
    useState(false);
  const [userSubcontractsGlobalFilter, setUserSubcontractsGlobalFilter] =
    useState("");

  // Set page title
  useEffect(() => {
    setPageTitle("Phân quyền");
  }, [setPageTitle]);

  // Function to load contracts with filtering
  const loadContracts = useCallback(
    async (pageIndex = 0, pageSize = 10, searchTerm = "") => {
      try {
        setContractsLoading(true);

        // Get list of contract IDs already assigned to the user
        const existingContractIds = (allUserContracts || []).map(
          (uc) => uc.contractId
        );

        // Prepare request data for the new API endpoint
        const requestData = {
          excludedContractIds: existingContractIds,
          userId: selectedUser?.userId,
          pageNumber: pageIndex + 1, // MaterialReactTable uses 0-based indexing
          pageSize: pageSize,
          sortColumn: "SignDate",
          sortDirection: "DESC",
        };

        // Add search term if provided
        if (searchTerm && searchTerm.trim()) {
          requestData.searchTerm = searchTerm.trim();
        }

        // Call the new backend API endpoint
        const response = await api.post(
          "/contract/available-for-user",
          requestData
        );

        if (response.data.success && response.data.data) {
          const availableContracts = response.data.data;
          const totalCount = response.data.totalCount || 0;

          // Set the contracts data for MaterialReactTable
          setContracts(availableContracts);
          setContractsTotal(totalCount);
        } else {
          console.error("Failed to load contracts:", response.data.message);
          setContracts([]);
          setContractsTotal(0);
        }
      } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
        console.error("Error loading contracts:", error);
        setContracts([]);
        setContractsTotal(0);
      } finally {
        setContractsLoading(false);
      }
    },
    [allUserContracts]
  );

  const loadSubcontracts = useCallback(
    async (pageIndex = 0, pageSize = 10, searchTerm = "") => {
      try {
        setSubcontractsLoading(true);

        const existingSubcontractIds = (allUserSubcontracts || []).map(
          (us) => us.subcontractId
        );

        const requestData = {
          userId: selectedUser.userId,
          excludedSubcontractIds: existingSubcontractIds,
          pageNumber: pageIndex + 1,
          pageSize,
          sortColumn: "SignDate",
          sortDirection: "DESC",
        };

        if (searchTerm && searchTerm.trim()) {
          requestData.searchTerm = searchTerm.trim();
        }

        const response = await api.post(
          "/Subcontract/available-for-user",
          requestData
        );

        if (response.data.success && response.data.data) {
          setSubcontracts(response.data.data);
          setSubcontractsTotal(response.data.totalCount || 0);
        } else {
          setSubcontracts([]);
          setSubcontractsTotal(0);
        }
      } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
        console.error("Error loading subcontracts:", error);
        setSubcontracts([]);
        setSubcontractsTotal(0);
      } finally {
        setSubcontractsLoading(false);
      }
    },
    [allUserSubcontracts, selectedUser]
  );

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload user contracts when selected user changes
  useEffect(() => {
    if (selectedUser) {
      // Reset pagination khi chọn user mới
      setUserContractsPagination({ pageIndex: 0, pageSize: 5 });
      setUserContractsGlobalFilter(""); // Reset search when user changes
      setUserSubcontractsPagination({ pageIndex: 0, pageSize: 5 });
      setUserSubcontractsGlobalFilter("");

      const hasUnrestrictedRole = getUserRoles(selectedUser.userId).some(
        (userRole) => userRole.isContractLimited === false
      );
      setContractFilter(!hasUnrestrictedRole);

      loadUserContracts(0, 5, "");
      loadUserSubcontracts(0, 5, "");
      // Load tất cả userContracts để chuẩn bị cho việc lọc
      loadAllUserContracts();
      loadAllUserSubcontracts();
    } else {
      setUserContracts([]);
      setUserContractsTotal(0);
      setAllUserContracts([]);
      setUserSubcontracts([]);
      setUserSubcontractsTotal(0);
      setAllUserSubcontracts([]);
      setContractFilter(true);
    }
  }, [selectedUser, userRoles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user contracts when pagination changes
  useEffect(() => {
    if (selectedUser) {
      loadUserContracts(
        userContractsPagination.pageIndex,
        userContractsPagination.pageSize,
        userContractsGlobalFilter
      );
    }
  }, [userContractsPagination, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUser) {
      loadUserSubcontracts(
        userSubcontractsPagination.pageIndex,
        userSubcontractsPagination.pageSize,
        userSubcontractsGlobalFilter
      );
    }
  }, [userSubcontractsPagination, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user contracts when search term changes
  useEffect(() => {
    if (selectedUser) {
      // Reset to first page when search term changes
      setUserContractsPagination((prev) => ({ ...prev, pageIndex: 0 }));
      loadUserContracts(
        0,
        userContractsPagination.pageSize,
        userContractsGlobalFilter
      );
    }
  }, [userContractsGlobalFilter, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedUser) {
      setUserSubcontractsPagination((prev) => ({ ...prev, pageIndex: 0 }));
      loadUserSubcontracts(
        0,
        userSubcontractsPagination.pageSize,
        userSubcontractsGlobalFilter
      );
    }
  }, [userSubcontractsGlobalFilter, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // derive selectedContracts (array of numeric contractIds) from controlled rowSelection
  useEffect(() => {
    const ids = Object.keys(rowSelection || {}).filter((k) => rowSelection[k]);
    const selected = ids.map((k) => {
      const n = Number(k);
      return isNaN(n) ? k : n;
    });
    setSelectedContracts(selected);
  }, [rowSelection]);

  // Load contracts when pagination changes
  useEffect(() => {
    if (addContractsOpen) {
      loadContracts(
        contractPagination.pageIndex,
        contractPagination.pageSize,
        contractsGlobalFilter
      );
    }
  }, [
    contractPagination,
    addContractsOpen,
    contractsGlobalFilter,
    loadContracts,
  ]);

  useEffect(() => {
    if (addSubcontractsOpen) {
      loadSubcontracts(
        subcontractPagination.pageIndex,
        subcontractPagination.pageSize,
        subcontractsGlobalFilter
      );
    }
  }, [
    subcontractPagination,
    addSubcontractsOpen,
    subcontractsGlobalFilter,
    loadSubcontracts,
  ]);

  // Load contracts when search term changes
  useEffect(() => {
    if (addContractsOpen) {
      // Reset to first page when search term changes
      setContractPagination((prev) => ({ ...prev, pageIndex: 0 }));
      loadContracts(0, contractPagination.pageSize, contractsGlobalFilter);
    }
  }, [
    contractsGlobalFilter,
    addContractsOpen,
    contractPagination.pageSize,
    loadContracts,
  ]);

  useEffect(() => {
    if (addSubcontractsOpen) {
      setSubcontractPagination((prev) => ({ ...prev, pageIndex: 0 }));
      loadSubcontracts(
        0,
        subcontractPagination.pageSize,
        subcontractsGlobalFilter
      );
    }
  }, [
    subcontractsGlobalFilter,
    addSubcontractsOpen,
    subcontractPagination.pageSize,
    loadSubcontracts,
  ]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadUsers(),
        loadApplications(),
        loadRoles(),
        loadUserRoles(),
        // loadContracts(), // Remove this - contracts will be loaded when modal opens
      ]);
    } catch (error) {
      showSnackbar("Lỗi khi tải dữ liệu", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/User?pageSize=1000&pageNumber=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading users:", error);
    }
  };

  const loadApplications = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Application/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApplications(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading applications:", error);
    }
  };

  const loadRoles = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/RolesManagement?pageSize=1000&pageNumber=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading roles:", error);
    }
  };

  const loadUserRoles = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole?pageSize=1000&pageNumber=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.data || []);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading user roles:", error);
    }
  };

  const loadUserContracts = async (
    pageIndex = 0,
    pageSize = 5,
    searchTerm = ""
  ) => {
    if (!selectedUser) {
      setUserContracts([]);
      setUserContractsTotal(0);
      return;
    }

    try {
      setUserContractsLoading(true);

      // Sử dụng api service tương tự ProjectList.js với phân trang thực sự
      const params = {
        pageNumber: pageIndex + 1, // API sử dụng 1-based indexing
        pageSize: pageSize,
        sortColumn: "ContractName",
        sortDirection: "ASC",
      };

      // Add search term if provided
      if (searchTerm && searchTerm.trim()) {
        params.searchTerm = searchTerm.trim();
      }

      const response = await api.get(
        `/UserContract/user/${selectedUser.userId}`,
        { params }
      );

      if (response.data.success) {
        setUserContracts(response.data.data || []);
        setUserContractsTotal(response.data.totalCount || 0);

      } else {
        console.error("Failed to load user contracts:", response.data.message);
        setUserContracts([]);
        setUserContractsTotal(0);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading user contracts:", error);
      setUserContracts([]);
      setUserContractsTotal(0);
    } finally {
      setUserContractsLoading(false);
    }
  };

  const loadUserSubcontracts = async (
    pageIndex = 0,
    pageSize = 5,
    searchTerm = ""
  ) => {
    if (!selectedUser) {
      setUserSubcontracts([]);
      setUserSubcontractsTotal(0);
      return;
    }

    try {
      setUserSubcontractsLoading(true);

      const params = {
        pageNumber: pageIndex + 1,
        pageSize,
        sortColumn: "SubcontractName",
        sortDirection: "ASC",
      };

      if (searchTerm && searchTerm.trim()) {
        params.searchTerm = searchTerm.trim();
      }

      const response = await api.get(
        `/UserSubcontract/user/${selectedUser.userId}`,
        { params }
      );

      if (response.data.success) {
        setUserSubcontracts(response.data.data || []);
        setUserSubcontractsTotal(response.data.totalCount || 0);
      } else {
        setUserSubcontracts([]);
        setUserSubcontractsTotal(0);
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading user subcontracts:", error);
      setUserSubcontracts([]);
      setUserSubcontractsTotal(0);
    } finally {
      setUserSubcontractsLoading(false);
    }
  };

  const getUserRoles = (userId) => {
    return userRoles.filter((ur) => ur.userId === userId);
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.roleId === roleId);
    return role ? role.roleName : "Unknown";
  };

  const getRoleDescription = (roleId) => {
    const role = roles.find((r) => r.roleId === roleId);
    return role ? role.description : "";
  };

  const getRoleApplicationName = (roleId) => {
    const role = roles.find((item) => Number(item.roleId) === Number(roleId));
    if (!role) return "Chưa xác định ứng dụng";

    return (
      role.applicationName ||
      role.ApplicationName ||
      getRoleApplication(role)?.applicationName ||
      "Chưa xác định ứng dụng"
    );
  };

  const getApplicationModuleCode = (application) => {
    const accessCode =
      application?.accessPermissionCode ||
      application?.AccessPermissionCode ||
      application?.applicationCode ||
      application?.ApplicationCode ||
      "";
    const codeParts = String(accessCode).toLowerCase().split(".");

    if (codeParts[0] === "pms" && codeParts.length > 1) {
      return codeParts[1];
    }

    return codeParts[0] || "";
  };

  const getRoleModuleCode = (role) => {
    const roleCode = role?.roleCode || role?.RoleCode || "";
    const codeParts = String(roleCode).toLowerCase().split(".");
    return codeParts[0] === "pms" ? codeParts[1] || "" : codeParts[0] || "";
  };

  const getRoleApplication = (role) => {
    const roleApplicationId = role.applicationId ?? role.ApplicationId;
    if (roleApplicationId) {
      const matchedApplication = applications.find(
        (application) => Number(application.applicationId) === Number(roleApplicationId)
      );
      if (matchedApplication) return matchedApplication;
    }

    const roleModuleCode = getRoleModuleCode(role);
    if (!roleModuleCode) return null;

    return applications.find(
      (application) => getApplicationModuleCode(application) === roleModuleCode
    );
  };

  const groupedRoles = useMemo(() => {
    const groupMap = new Map();

    roles.forEach((role) => {
      const application = getRoleApplication(role);
      const groupKey = application?.applicationId || getRoleModuleCode(role) || "unassigned";
      const groupName =
        application?.applicationName ||
        getRoleModuleCode(role) ||
        "Vai trò chưa gán ứng dụng";

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          key: groupKey,
          name: groupName,
          roles: [],
        });
      }

      groupMap.get(groupKey).roles.push(role);
    });

    return Array.from(groupMap.values())
      .map((group) => ({
        ...group,
        roles: group.roles.sort((left, right) =>
          String(left.roleName || "").localeCompare(String(right.roleName || ""), "vi", {
            sensitivity: "base",
          })
        ),
      }))
      .sort((left, right) =>
        String(left.name || "").localeCompare(String(right.name || ""), "vi", {
          sensitivity: "base",
        })
      );
  }, [roles, applications]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddRole = async () => {
    if (!selectedUser) return;

    // Kiểm tra quyền động
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar("Bạn không có quyền thêm vai trò.", "warning");
      return;
    }

    setSelectedRoleIds([]);
    setSelectedContracts([]);
    setDialogOpen(true);
  };

  const handleSaveUserRole = async () => {
    if (!selectedUser || selectedRoleIds.length === 0) return;

    // Kiểm tra quyền động
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar("Bạn không có quyền thêm vai trò.", "warning");
      return;
    }

    setSaving(true);
    try {
      const token = getToken();

      const requestData = {
        userId: selectedUser.userId,
        roleIds: selectedRoleIds.map(Number),
        isContractLimited: true,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole/bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to assign role");
      }

      const responseData = await response.json();
      showSnackbar(responseData.message || "Đã gán các vai trò thành công", "success");
      setDialogOpen(false);
      await loadUserRoles();
      await loadUserContracts(
        userContractsPagination.pageIndex,
        userContractsPagination.pageSize,
        userContractsGlobalFilter
      );
    } catch (error) {
      showSnackbar(error.message || "Lỗi khi gán vai trò", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUserContract = async (userContractId) => {
    // Kiểm tra quyền
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar(
        "Bạn không có quyền xóa hợp đồng được phân cho người dùng.",
        "warning"
      );
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserContract/${userContractId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showSnackbar("Đã xóa hợp đồng được gán", "success");
        await loadUserContracts(
          userContractsPagination.pageIndex,
          userContractsPagination.pageSize,
          userContractsGlobalFilter
        );
      } else {
        throw new Error("Failed to delete mapping");
      }
    } catch (error) {
      showSnackbar("Lỗi khi xóa mapping", "error");
    }
  };

  const handleRemoveUserRole = async (userRoleId) => {
    // Kiểm tra quyền động
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar("Bạn không có quyền xóa vai trò.", "warning");
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserRole/${userRoleId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showSnackbar("Đã xóa vai trò thành công", "success");
        await loadUserRoles();
      } else {
        throw new Error("Failed to remove role");
      }
    } catch (error) {
      showSnackbar("Lỗi khi xóa vai trò", "error");
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  // Hàm load tất cả userContracts để lọc trong dialog
  const loadAllUserContracts = async () => {
    if (!selectedUser) {
      setAllUserContracts([]);
      return [];
    }

    try {
      // Load tất cả userContracts với pageSize lớn
      const params = {
        pageNumber: 1,
        pageSize: 1000, // Load tất cả
        sortColumn: "ContractName",
        sortDirection: "ASC",
      };

      const response = await api.get(
        `/UserContract/user/${selectedUser.userId}`,
        { params }
      );

      if (response.data.success) {
        const allContracts = response.data.data || [];

        setAllUserContracts(allContracts);
        return allContracts;
      } else {
        console.error(
          "Failed to load all user contracts:",
          response.data.message
        );
        setAllUserContracts([]);
        return [];
      }
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading all user contracts:", error);
      setAllUserContracts([]);
      return [];
    }
  };

  const handleDeleteUserSubcontract = async (userSubcontractId) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (!result.hasPermission) {
      showSnackbar(
        "Bạn không có quyền xóa giao khoán được phân cho người dùng.",
        "warning"
      );
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/UserSubcontract/${userSubcontractId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showSnackbar("Đã xóa giao khoán được gán", "success");
        await loadUserSubcontracts(
          userSubcontractsPagination.pageIndex,
          userSubcontractsPagination.pageSize,
          userSubcontractsGlobalFilter
        );
        await loadAllUserSubcontracts();
      } else {
        throw new Error("Failed to delete mapping");
      }
    } catch (error) {
      showSnackbar("Lỗi khi xóa mapping giao khoán", "error");
    }
  };

  const loadAllUserSubcontracts = async () => {
    if (!selectedUser) {
      setAllUserSubcontracts([]);
      return [];
    }

    try {
      const params = {
        pageNumber: 1,
        pageSize: 1000,
        sortColumn: "SubcontractName",
        sortDirection: "ASC",
      };

      const response = await api.get(
        `/UserSubcontract/user/${selectedUser.userId}`,
        { params }
      );

      if (response.data.success) {
        const allSubcontracts = response.data.data || [];
        setAllUserSubcontracts(allSubcontracts);
        return allSubcontracts;
      }

      setAllUserSubcontracts([]);
      return [];
    } catch (error) {
      showSnackbar("Không thể tải đầy đủ dữ liệu phân quyền. Hãy tải lại trước khi lưu.", "error");
      console.error("Error loading all user subcontracts:", error);
      setAllUserSubcontracts([]);
      return [];
    }
  };

  // Hàm xử lý thêm hợp đồng với kiểm tra phân quyền
  const handleAddContracts = async () => {
    if (!selectedUser) {
      showSnackbar("Vui lòng chọn người dùng trước", "warning");
      return;
    }

    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (result.hasPermission) {
      // Load tất cả userContracts để đảm bảo lọc chính xác
      await loadAllUserContracts();

      setAddRowSelection({});
      setContractsGlobalFilter("");
      // Reset pagination to first page when opening modal
      setContractPagination({ pageIndex: 0, pageSize: 10 });
      setAddContractsOpen(true);
    } else {
      showSnackbar(
        "Bạn không có quyền thêm hợp đồng cho người dùng.",
        "warning"
      );
    }
  };

  const handleAddSubcontracts = async () => {
    if (!selectedUser) {
      showSnackbar("Vui lòng chọn người dùng trước", "warning");
      return;
    }

    const result = await checkPermission(PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_ASSIGN);
    if (result.hasPermission) {
      await loadAllUserSubcontracts();
      setAddSubcontractRowSelection({});
      setSubcontractsGlobalFilter("");
      setSubcontractPagination({ pageIndex: 0, pageSize: 10 });
      setAddSubcontractsOpen(true);
    } else {
      showSnackbar(
        "Bạn không có quyền thêm giao khoán cho người dùng.",
        "warning"
      );
    }
  };

  // Các vai trò đã có được giữ nguyên khi gán thêm theo lô.

  const getAlreadyAssignedSelectedRoles = () => {
    if (!selectedUser || selectedRoleIds.length === 0) return [];
    const assignedRoleIds = new Set(
      getUserRoles(selectedUser.userId).map((userRole) => Number(userRole.roleId))
    );

    return selectedRoleIds.filter((roleId) => assignedRoleIds.has(Number(roleId)));
  };

  const handleContractFilterChange = async (nextValue) => {
    if (!selectedUser || saving || nextValue === contractFilter) return;

    if (userContractsTotal > 0) {
      const confirmed = await confirm({
        title: "Xác nhận thay đổi giới hạn hợp đồng",
        message: nextValue
          ? "Thao tác này sẽ xóa toàn bộ hợp đồng đang được gán cho người dùng. Bạn có thể gán lại từng hợp đồng sau đó."
          : "Thao tác này sẽ thay danh sách hiện tại bằng tất cả hợp đồng và tự gán hợp đồng mới về sau.",
        type: "warning",
      });
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      await api.patch(
        `/UserRole/user/${selectedUser.userId}/contract-filter`,
        { contractFilter: nextValue },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setContractFilter(nextValue);
      await loadUserRoles();
      await loadUserContracts(
        userContractsPagination.pageIndex,
        userContractsPagination.pageSize,
        userContractsGlobalFilter
      );
      await loadAllUserContracts();
      showSnackbar("Đã cập nhật giới hạn hợp đồng", "success");
    } catch (error) {
      showSnackbar(error.response?.data?.message || "Lỗi khi cập nhật giới hạn hợp đồng", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="user-role-management tab-content">
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Left Panel - Users List */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <GroupIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Danh sách người dùng
                </Typography>
              </Box>

              <Tooltip title="Tải lại dữ liệu">
                <AppButton color="inherit" onClick={loadData}><RefreshIcon /></AppButton>
              </Tooltip>
            </Box>

            <AppTextField
              placeholder="Tìm kiếm người dùng ..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              sx={{ flex: 1, mb: 2 }}
              startIcon={<SearchIcon fontSize="small" />}
            />
            <UserTable
              users={users.filter(
                (u) =>
                  u.userName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  u.fullName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  u.email?.toLowerCase().includes(searchTerm.toLowerCase())
              )}
              onSelectUser={setSelectedUser}
              selectedUser={selectedUser}
            />
          </Paper>
        </Grid>

        {/* Right Panel - User Roles */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {selectedUser
                  ? `Vai trò của: ${selectedUser.fullName}`
                  : "Chọn người dùng để xem vai trò"}
              </Typography>

              {selectedUser && (
                <AppButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddRole}
                  sx={{ textTransform: "none", mb: 2 }}
                >
                  Thêm vai trò
                </AppButton>
              )}
            </Box>

            {selectedUser ? (
              <Box>
                {getUserRoles(selectedUser.userId).length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <GroupIcon sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Người dùng chưa có vai trò nào
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Nhấp "Thêm vai trò" để gán vai trò cho người dùng
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {getUserRoles(selectedUser.userId).map((userRole) => (
                      <ListItem
                        key={userRole.userRoleId}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          mb: 1,
                          display: "block",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Chip
                            label={getRoleName(userRole.roleId)}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(getRoleName(userRole.roleId)),
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip title="Xóa vai trò">
                            <IconButton
                              onClick={() =>
                                handleRemoveUserRole(userRole.userRoleId)
                              }
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {getRoleApplicationName(userRole.roleId)} - {" "}
                          {getRoleDescription(userRole.roleId) || "Chưa có mô tả vai trò"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Giới hạn hợp đồng: {userRole.isContractLimited === false ? "Không" : "Có"}
                        </Typography>

                      </ListItem>
                    ))}
                  </List>
                )}

                {/* UserContracts table */}
                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Hợp đồng được gán cho người dùng
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {getUserRoles(selectedUser.userId).length > 0 && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={contractFilter}
                              onChange={(event) => handleContractFilterChange(event.target.checked)}
                              disabled={saving}
                              size="small"
                            />
                          }
                          label="Giới hạn hợp đồng"
                          sx={{ margin: 0 }}
                        />
                      )}
                      <AppButton
                        onClick={handleAddContracts}
                        variant="outlined"
                      >
                        Thêm hợp đồng
                      </AppButton>
                    </Box>
                  </Box>
                  {userContracts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Người dùng chưa được gán hợp đồng nào
                    </Typography>
                  ) : (
                    <DataTable
                      columns={[
                        {
                          accessorKey: "contractNumber",
                          header: "Số hợp đồng",
                          size: 100
                        },
                        {
                          accessorKey: "contractName",
                          header: "Tên hợp đồng",
                        },
                        {
                          id: "actions",
                          header: "Thao tác",
                          enableSorting: false,
                          enableColumnActions: false,
                          Cell: ({ row }) => (
                            <Tooltip title="Xóa hợp đồng">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteUserContract(
                                    row.original.userContractId
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ),
                        },
                      ]}
                      data={userContracts.map((uc) => ({
                        userContractId: uc.userContractId,
                        contractId: uc.contractId,
                        contractNumber:
                          uc.contractNumber || uc.contractCode || "",
                        contractName: uc.contractName || "",
                        notes: uc.notes,
                      }))}
                      getRowId={(row) => row.userContractId}
                      manualPagination={true}
                      rowCount={userContractsTotal}
                      enableColumnActions={false}
                      enableGlobalFilter={true}
                      manualFiltering={true}
                      enableSorting={false}
                      state={{
                        pagination: userContractsPagination,
                        isLoading: userContractsLoading,
                        globalFilter: userContractsGlobalFilter,
                      }}
                      onPaginationChange={setUserContractsPagination}
                      onGlobalFilterChange={setUserContractsGlobalFilter}
                    />
                  )}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Giao khoán được gán cho người dùng
                    </Typography>
                    <AppButton
                      onClick={handleAddSubcontracts}
                      variant="outlined"
                    >
                      Thêm giao khoán
                    </AppButton>
                  </Box>
                  {userSubcontracts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Người dùng chưa được gán giao khoán nào
                    </Typography>
                  ) : (
                    <DataTable
                      columns={[
                        {
                          accessorKey: "subcontractNumber",
                          header: "Số giao khoán",
                          size: 100
                        },
                        {
                          accessorKey: "subcontractName",
                          header: "Tên giao khoán",
                        },
                        {
                          id: "actions",
                          header: "Thao tác",
                          enableSorting: false,
                          enableColumnActions: false,
                          Cell: ({ row }) => (
                            <Tooltip title="Xóa giao khoán">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteUserSubcontract(
                                    row.original.userSubcontractId
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          ),
                        },
                      ]}
                      data={userSubcontracts.map((us) => ({
                        userSubcontractId: us.userSubcontractId,
                        subcontractId: us.subcontractId,
                        subcontractNumber: us.subcontractNumber || "",
                        subcontractName: us.subcontractName || "",
                        status: us.status || "",
                      }))}
                      getRowId={(row) => row.userSubcontractId}
                      manualPagination={true}
                      rowCount={userSubcontractsTotal}
                      enableColumnActions={false}
                      enableGlobalFilter={true}
                      manualFiltering={true}
                      enableSorting={false}
                      state={{
                        pagination: userSubcontractsPagination,
                        isLoading: userSubcontractsLoading,
                        globalFilter: userSubcontractsGlobalFilter,
                      }}
                      onPaginationChange={setUserSubcontractsPagination}
                      onGlobalFilterChange={setUserSubcontractsGlobalFilter}
                    />
                  )}
                </Box>

                {/* Dialog: Add contracts to user */}
                <AppDialog
                  open={addContractsOpen}
                  onClose={() => setAddContractsOpen(false)}
                  title={`Thêm hợp đồng cho ${selectedUser?.fullName || ""}`}
                  size="md"
                  loading={saving}
                  actions={
                    <>
                      <AppButton disabled={saving} variant="outlined" onClick={() => setAddContractsOpen(false)}>
                        Hủy
                      </AppButton>
                      <AppButton disabled={saving}
                        variant="contained"
                        onClick={async () => {
                          if (!selectedUser) return;
                          const ids = Object.keys(addRowSelection || {})
                            .filter((k) => addRowSelection[k])
                            .map((k) => Number(k));

                          if (ids.length === 0) {
                            showSnackbar(
                              "Vui lòng chọn ít nhất 1 hợp đồng",
                              "warning"
                            );
                            return;
                          }

                          setSaving(true);
                          try {
                            const token = getToken();
                            for (const cid of ids) {
                              const req = {
                                userId: selectedUser.userId,
                                contractId: cid,
                              };
                              const r = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/UserContract`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                  },
                                  body: JSON.stringify(req),
                                }
                              );
                              if (!r.ok) {
                                const e = await r.json();
                                throw new Error(
                                  e.message || "Failed to add contract"
                                );
                              }
                            }
                            showSnackbar(
                              "Đã thêm hợp đồng cho người dùng",
                              "success"
                            );
                            setAddContractsOpen(false);
                            await loadUserContracts(
                              userContractsPagination.pageIndex,
                              userContractsPagination.pageSize,
                              userContractsGlobalFilter
                            );
                          } catch (err) {
                            showSnackbar(
                              err.message || "Lỗi khi thêm hợp đồng",
                              "error"
                            );
                          } finally {
                            setSaving(false);
                          }
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Thêm
                      </AppButton>
                    </>
                  }
                >
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Chọn các hợp đồng để thêm cho người dùng (chọn nhiều)
                      </Typography>
                      <DataTable
                        columns={[
                          { accessorKey: "contractNumber", header: "Số hợp đồng", size: 150, },
                          { accessorKey: "contractName", header: "Tên hợp đồng", size: 300, },
                        ]}
                        data={contracts.map((c) => ({
                          contractId: c.contractId,
                          contractNumber:
                            c.contractCode || c.contractNumber || "",
                          contractName: c.contractName || "",
                        }))}
                        enableRowSelection
                        getRowId={(row) => row.contractId}
                        state={{
                          rowSelection: addRowSelection,
                          pagination: contractPagination,
                          isLoading: contractsLoading,
                          globalFilter: contractsGlobalFilter,
                        }}
                        onRowSelectionChange={setAddRowSelection}
                        onPaginationChange={setContractPagination}
                        onGlobalFilterChange={setContractsGlobalFilter}
                        enablePagination={true}
                        manualPagination={true}
                        rowCount={contractsTotal}
                        enableColumnActions={false}
                        enableGlobalFilter={true}
                        manualFiltering={false}
                        enableSorting={false}
                      />
                    </Box>
                </AppDialog>

                <AppDialog
                  open={addSubcontractsOpen}
                  onClose={() => setAddSubcontractsOpen(false)}
                  title={`Thêm giao khoán cho ${selectedUser?.fullName || ""}`}
                  size="md"
                  loading={saving}
                  actions={
                    <>
                      <AppButton disabled={saving} variant="outlined" color="inherit" onClick={() => setAddSubcontractsOpen(false)}>
                        Hủy
                      </AppButton>
                      <AppButton disabled={saving}
                        variant="contained"
                        onClick={async () => {
                          if (!selectedUser) return;
                          const ids = Object.keys(addSubcontractRowSelection || {})
                            .filter((k) => addSubcontractRowSelection[k])
                            .map((k) => Number(k));

                          if (ids.length === 0) {
                            showSnackbar(
                              "Vui lòng chọn ít nhất 1 giao khoán",
                              "warning"
                            );
                            return;
                          }

                          setSaving(true);
                          try {
                            const token = getToken();
                            for (const subcontractId of ids) {
                              const req = {
                                userId: selectedUser.userId,
                                subcontractId,
                              };
                              const r = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/UserSubcontract`,
                                {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                  },
                                  body: JSON.stringify(req),
                                }
                              );
                              if (!r.ok) {
                                const e = await r.json();
                                throw new Error(
                                  e.message || "Failed to add subcontract"
                                );
                              }
                            }
                            showSnackbar(
                              "Đã thêm giao khoán cho người dùng",
                              "success"
                            );
                            setAddSubcontractsOpen(false);
                            await loadUserSubcontracts(
                              userSubcontractsPagination.pageIndex,
                              userSubcontractsPagination.pageSize,
                              userSubcontractsGlobalFilter
                            );
                            await loadAllUserSubcontracts();
                          } catch (err) {
                            showSnackbar(
                              err.message || "Lỗi khi thêm giao khoán",
                              "error"
                            );
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        Thêm
                      </AppButton>
                    </>
                  }
                >
                    <Box sx={{ pt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Chọn các giao khoán để thêm cho người dùng (chọn nhiều)
                      </Typography>
                      <DataTable
                        columns={[
                          { accessorKey: "subcontractNumber", header: "Số giao khoán", size: 150, },
                          { accessorKey: "subcontractName", header: "Tên giao khoán", size: 300, },
                        ]}
                        data={subcontracts.map((s) => ({
                          subcontractId: s.subcontractId,
                          subcontractNumber: s.subcontractNumber || "",
                          subcontractName: s.subcontractName || "",
                        }))}
                        enableRowSelection
                        getRowId={(row) => row.subcontractId}
                        state={{
                          rowSelection: addSubcontractRowSelection,
                          pagination: subcontractPagination,
                          isLoading: subcontractsLoading,
                          globalFilter: subcontractsGlobalFilter,
                        }}
                        onRowSelectionChange={setAddSubcontractRowSelection}
                        onPaginationChange={setSubcontractPagination}
                        onGlobalFilterChange={setSubcontractsGlobalFilter}
                        enablePagination={true}
                        manualPagination={true}
                        rowCount={subcontractsTotal}
                        enableColumnActions={false}
                        enableGlobalFilter={true}
                        manualFiltering={false}
                        enableSorting={false}
                      />
                    </Box>

                </AppDialog>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <PersonIcon sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Chọn một người dùng để quản lý vai trò
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Role Dialog */}
      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={`Thêm vai trò cho ${selectedUser?.fullName || ""}`}
        size="lg"
        loading={saving}
        actions={
          <>
            <AppButton disabled={saving} variant="outlined" onClick={() => setDialogOpen(false)}>Hủy</AppButton>
            <AppButton
              onClick={handleSaveUserRole}
              variant="contained"
              disabled={
                saving || selectedRoleIds.length === 0
              }
            >
              {saving ? "Đang lưu..." : "Thêm vai trò"}
            </AppButton>
          </>
        }
      >
          <Box sx={{ pt: 1 }}>
            <>
              <AppAutocomplete disabled={saving}
                value={selectedRoleIds}
                label="Vai trò"
                multiple
                onChange={(values) => setSelectedRoleIds(values)}
                options={groupedRoles.flatMap((group) =>
                  group.roles.map((role) => ({
                    value: role.roleId,
                    label: role.roleName,
                    description: role.description || role.roleCode,
                    groupName: group.name,
                  }))
                )}
                groupBy={(option) => option.groupName}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;

                  return (
                    <Box component="li" key={key} {...optionProps}>
                      <Box>
                        <Typography variant="body1" fontSize={"12px"}>{option.label}</Typography>
                        <Typography variant="caption" fontSize={"11px"} color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                sx={{ mb: 2 }}
              />
            </>

            {getAlreadyAssignedSelectedRoles().length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Một số vai trò đã được gán trước đó và sẽ được giữ nguyên.
              </Alert>
            )}
          </Box>
      </AppDialog>

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default UserRoleManagement;
